import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useMemo } from 'react';
import * as v from 'valibot';

import presents from '@/assets/images/landingPages/adventCalendar/icons/presents.svg';
import snowflakesMobile from '@/assets/images/landingPages/adventCalendar/snowflakes.svg';
import snowflakesDesktop from '@/assets/images/landingPages/adventCalendar/snowflakes_short.svg';
import { AdventSummarySchema } from '@/competitions/2023/december/pages/LandingPage/schemas.ts';
import ActionButton from '@/components/ActionButton/index.ts';
import { LoanExampleText } from '@/components/LoanExampleText/index.ts';

const RedText = styled('span')(({ theme }) => ({
  color: theme.palette.accent.red,
}));

const Title = styled('h2')(({ theme }) => ({
  color: theme.palette.common.white,
  textAlign: 'center',
  fontWeight: 700,
  marginBottom: theme.spacing(1),
  fontSize: '1.563rem',
  [theme.breakpoints.up('sm')]: {
    fontSize: '2.438rem',
    marginBottom: theme.spacing(1),
  },
}));

const StyledActionButton = styled(ActionButton)(({ theme }) => ({
  minWidth: 280,
  fontSize: '19px',
  maxHeight: '55px',
  [theme.breakpoints.up('sm')]: {
    minWidth: 342,
  },
}));

const SnowyContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  paddingTop: theme.spacing(1),
  marginRight: theme.spacing(1),
  width: '29%',
  [theme.breakpoints.down('lg')]: {
    width: '93%',
  },
}));

const SnowflakeImage = styled('img')({
  width: '100%',
  height: '100%',
});

const ButtonContainer = styled('div')(({ theme }) => ({
  flexDirection: 'column',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
  backgroundColor: '#1B5A53',
  borderRadius: '20px',
  padding: '16px 8px',
  width: '85%',
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(3),
  [theme.breakpoints.between(1200, 1430)]: {
    width: '99%',
    marginBottom: theme.spacing(1.5),
  },
  [theme.breakpoints.down(1200)]: {
    width: '70%',
    marginBottom: theme.spacing(3),
  },
  [theme.breakpoints.down(1000)]: {
    marginBottom: theme.spacing(2),
    width: '80%',
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(2),
    width: '100%',
    padding: '16px 8px',
  },
}));

const ButtonDescription = styled('p')(({ theme }) => ({
  textAlign: 'center',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(3),
  fontSize: '19px',
  fontWeight: 700,
  color: theme.palette.common.white,
  width: '90%',
  lineHeight: '1.15',
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    width: '85%',
  },
}));

const CompetitionDescription = styled('p')(({ theme }) => ({
  paddingBottom: theme.spacing(2),
  paddingTop: theme.spacing(0.5),
  fontSize: '19px',
  lineHeight: '1.15',
  color: theme.palette.common.white,
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
  },
}));

const CompetitionAmount = styled('p')(({ theme }) => ({
  color: theme.palette.common.white,
  paddingTop: theme.spacing(2),
  fontSize: '2.438rem',
  fontWeight: 700,
  lineHeight: '1.15',
  [theme.breakpoints.down('sm')]: {
    fontSize: '22px',
  },
}));

const CompetitionAmountSmall = styled('p')(({ theme }) => ({
  color: theme.palette.common.white,
  fontSize: '1.563rem',
  fontWeight: 700,
  lineHeight: '1.15',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
  },
}));

const LoanExampleTextWrapper = styled('div')(({ theme }) => ({
  color: '#F0F4FAB2',
  textAlign: 'center',
  fontSize: '1rem',
  paddingTop: theme.spacing(3),
  lineHeight: '1.15',
  [theme.breakpoints.between('md', 'lg')]: {
    width: '80%',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    width: '85%',
  },
}));

const PresentsImage = styled('img')(({ theme }) => ({
  width: '91px',
  height: '83px',
  marginTop: theme.spacing(6),
  [theme.breakpoints.down('md')]: {
    width: '80px',
    height: '75px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '75px',
    height: '70px',
  },
}));

interface EndOfCompetitionProps {
  meta: unknown;
}

const EndOfCompetition = ({ meta }: EndOfCompetitionProps) => {
  const summary = useMemo(() => v.parse(AdventSummarySchema, meta ?? {}), [meta]);
  const largeScreen = useMediaQuery((theme) => theme.breakpoints.up('sm'));

  return (
    <>
      <SnowyContainer>
        <SnowflakeImage
          src={largeScreen ? snowflakesDesktop : snowflakesMobile}
          alt="Background snowflakes"
        />
      </SnowyContainer>
      <PresentsImage src={presents} alt="Tree" />

      <Title>
        Oppsummering
        <br /> Scoopr's <RedText>Julekalender </RedText>
      </Title>

      {summary.totalGivenMoney ? (
        <>
          <CompetitionAmount>{summary.totalGivenMoney}</CompetitionAmount>
          <CompetitionDescription>delt ut av Scoopr</CompetitionDescription>
        </>
      ) : null}

      {summary.totalCutExpenses ? (
        <>
          <CompetitionAmountSmall>{summary.totalCutExpenses}</CompetitionAmountSmall>
          <CompetitionDescription>kuttet i rentekost på kredittkortgjeld</CompetitionDescription>
        </>
      ) : null}

      <ButtonContainer>
        <ButtonDescription>
          Selv om julekalenderen er avsluttet kan du fortsatt "vinne" med å senke renten.
        </ButtonDescription>
        <StyledActionButton
          label="Se om jeg kan få lavere rente"
          step="skip_start"
          flow="debt_check"
          color="secondary"
          gaEvent="advent_summary_cta_clicked"
          labelClassName="action-button-text"
          sx={{
            '& .action-button-text': {
              fontWeight: 600,
              fontSize: {
                xs: '16px',
                sm: '22px',
              },
            },
          }}
        />
        <LoanExampleTextWrapper>
          <LoanExampleText />
        </LoanExampleTextWrapper>
      </ButtonContainer>
    </>
  );
};

export default EndOfCompetition;
